<template>
    <header
        ref="siteHeader"
        class="header"
        :class="{
            'header--scrolled': !isAtTop
        }"
    >
        <div class="container">
            <div class="header-grid">
                <div
                    v-if="topNavigationData.length > 0 || $slots.topcontrols"
                    class="header-grid__secondary-navigation header__secondary-navigation"
                >
                    <div class="secondary-navigation__row">
                        <nav
                            v-if="topNavigationData.length > 0"
                            class="secondary-navigation"
                            aria-label="Secondary navigation"
                        >
                            <ul class="secondary-navigation__list">
                                <li
                                    v-for="item in topNavigationData"
                                    :key="item.id ?? item.url"
                                    class="secondary-navigation__item"
                                >
                                    <a
                                        class="secondary-navigation__link"
                                        :href="item.url"
                                        :target="item.target || null"
                                        :rel="item.target === '_blank' ? 'noopener noreferrer' : null"
                                    >
                                        {{ item.name }}
                                    </a>
                                </li>
                                <li v-if="hasReciteMe" class="secondary-navigation__item">
                                    <button
                                        class="secondary-navigation__link reciteme-button"
                                        type="button"
                                        @click="loadReciteMe"
                                        aria-label="Accessibility tools"
                                    >
                                        Accessibility
                                    </button>
                                </li>
                            </ul>
                        </nav>

                        <div v-if="$slots.topcontrols" class="secondary-navigation__extras">
                            <slot name="topcontrols"></slot>
                        </div>
                    </div>
                </div>

                <div class="header-grid__logo">
                    <a :href="logoUrl" class="header__logo logo" aria-label="Go to home page">
                        <slot name="site-logo"></slot>
                    </a>
                </div>

                <div class="header-grid__buttons">
                    <slot name="buttons"></slot>
                </div>

                <div class="header-grid__navigation">
                    <template v-if="showMegaNav && navigationData.length > 0">
                        <MegaNavigation :navigationData="navigationData" />
                    </template>

                    <DrawerNavigation
                        v-if="showMobileNav && navigationData.length > 0"
                        :navigationData="navigationData"
                    >
                        <template #mobilecontrols>
                            <slot name="mobilenavcontrols"></slot>
                        </template>
                    </DrawerNavigation>
                </div>
            </div>
        </div>
    </header>
</template>

<script>
import DrawerNavigation from '../components/DrawerNavigation.vue';
import MegaNavigation from '../components/MegaNavigation.vue';

function debounce(func, delay) {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
    };
}

export default {
    name: 'SiteHeader',
    components: {
        DrawerNavigation,
        MegaNavigation
    },
    props: {
        navigationData: {
            type: Array,
            required: true,
            default: () => []
        },
        topNavigationData: {
            type: Array,
            required: false,
            default: () => []
        },
        hasReciteMe: {
            type: Boolean,
            required: false,
            default: false
        },
        logoUrl: {
            type: String,
            required: false,
            default: '/'
        }
    },
    data() {
        return {
            showMobileNav: false,
            showMegaNav: false,
            isAtTop: true,
            showStickyNav: false,
            isHeaderInView: true
        };
    },
    methods: {
        loadReciteMe() {
            if (typeof window.loadService === 'function') {
                window.loadService();
            }
        },
        checkScreenWidth() {
            this.showMobileNav = window.innerWidth < 1200;
            this.showMegaNav = window.innerWidth >= 1200;
        },
        handleScroll() {
            const currentY = window.scrollY || 0;
            this.isAtTop = currentY === 0;
        },
        handleResize() {
            this.checkScreenWidth();
        }
    },
    mounted() {
        this.debouncedResizeHandler = debounce(this.handleResize, 100);

        this.handleResize();
        this.handleScroll();

        window.addEventListener('resize', this.debouncedResizeHandler);
        window.addEventListener('orientationchange', this.handleResize);
        window.addEventListener('scroll', this.handleScroll);

        const headerEl = this.$refs.siteHeader;

        if (headerEl && 'IntersectionObserver' in window) {
            this.headerObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        this.isHeaderInView = entry.isIntersecting;

                        if (entry.isIntersecting) {
                            this.showStickyNav = false;
                        } else {
                            this.showStickyNav = true;
                        }
                    });
                },
                {
                    root: null,
                    threshold: 0
                }
            );

            this.headerObserver.observe(headerEl);
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.debouncedResizeHandler);
        window.removeEventListener('orientationchange', this.handleResize);
        window.removeEventListener('scroll', this.handleScroll);

        if (this.headerObserver) {
            this.headerObserver.disconnect();
        }
    }
};
</script>